import apiClient from "./apiClient";

// Use centralized API client for consistent error handling
const api = apiClient;

// Create a new subscription
export async function createSubscription(data) {
  // data = { organizationId, paymentMethodId, billingEmail, maxRooms }
  const response = await api.post("/api/subscriptions/create", data);
  return response.data;
}

// Get subscription details for organization
export async function getSubscription(organizationId) {
  const response = await api.get(`/api/subscriptions/${organizationId}`);
  return response.data;
}

// Get subscription status (lightweight check)
export async function getSubscriptionStatus(organizationId) {
  const response = await api.get(`/api/subscriptions/status/${organizationId}`);
  return response.data;
}

// Create billing portal session
export async function createBillingPortalSession(organizationId) {
  const response = await api.post("/api/subscriptions/billing-portal", {
    organizationId
  });
  return response.data;
}

// Preview cancellation with proration charges
export async function previewCancellation(organizationId) {
  const response = await api.post("/api/subscriptions/preview-cancellation", {
    organizationId
  });
  return response.data;
}

// Cancel subscription
export async function cancelSubscription(organizationId, cancelImmediately = false) {
  const response = await api.post("/api/subscriptions/cancel", {
    organizationId,
    cancelImmediately
  });
  return response.data;
}

// Reactivate subscription
export async function reactivateSubscription(organizationId) {
  const response = await api.post("/api/subscriptions/reactivate", {
    organizationId
  });
  return response.data;
}

// Sync payments from Stripe
export async function syncPaymentsFromStripe(organizationId) {
  const response = await api.post(`/api/payments/sync/${organizationId}`);
  return response.data;
}

// Get payment history (last 3 invoices from Stripe)
export async function getPaymentHistory(organizationId) {
  const response = await api.get(`/api/payments/history/${organizationId}`);
  return response.data;
}

// Preview proration for room count changes
export async function previewProration(organizationId, newRoomCount) {
  const response = await api.post("/api/subscriptions/preview-proration", {
    organizationId,
    newRoomCount
  });
  return response.data;
}

// Update room count with proration confirmation
export async function updateRoomCountWithProration(organizationId, newRoomCount, confirmProration = true) {
  const response = await api.post("/api/subscriptions/update-room-count", {
    organizationId,
    newRoomCount,
    confirmProration
  });
  return response.data;
}

// Helper function to format currency
export function formatCurrency(amountInCents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amountInCents / 100);
}

// Helper function to format amount in dollars
export function formatAmount(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

// Helper function to format date
export function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Get next billing preview
export const getNextBillingPreview = async (organizationId) => {
  try {
    const response = await api.get(`/api/subscriptions/organizations/${organizationId}/next-billing`);
    return response.data;
  } catch (error) {
    console.error('Error getting next billing preview:', error);
    throw error;
  }
};

// Update subscription plan
export const previewPlanChange = async (organizationId, newMaxRooms) => {
  // Guard against invalid organizationId
  if (!organizationId || organizationId === 'undefined' || organizationId === 'null') {
    throw new Error('Invalid organization ID provided');
  }

  try {
    const response = await api.post(`/api/subscriptions/organizations/${organizationId}/plan/preview`, {
      maxRooms: newMaxRooms
    });
    return response.data;
  } catch (error) {
    console.error('Error previewing plan change:', error);
    throw error;
  }
};

export const updateSubscriptionPlan = async (organizationId, maxRooms) => {
  try {
    const response = await api.post(`/api/subscriptions/organizations/${organizationId}/plan`, {
      maxRooms
    });
    return response.data;
  } catch (error) {
    console.error('Error updating subscription plan:', error);
    throw error;
  }
};

// Update billing email
export const updateBillingEmail = async (organizationId, email) => {
  try {
    const response = await api.patch(`/api/subscriptions/organizations/${organizationId}/billing-email`, {
      email
    });
    return response.data;
  } catch (error) {
    console.error('Error updating billing email:', error);
    throw error;
  }
};

// Update payment method
export const updatePaymentMethod = async (organizationId, paymentMethodId) => {
  try {
    const response = await api.patch(`/api/subscriptions/organizations/${organizationId}/payment-method`, {
      paymentMethodId
    });
    return response.data;
  } catch (error) {
    console.error('Error updating payment method:', error);
    throw error;
  }
};

// Get payment method details
export const getPaymentMethod = async (organizationId) => {
  try {
    const response = await api.get(`/api/subscriptions/organizations/${organizationId}/payment-method`);
    return response.data;
  } catch (error) {
    console.error('Error getting payment method:', error);
    throw error;
  }
}; 